import { config } from '@abyss/web/tools/config';
import enLangugages from '@optum-fpc-psx-mobile-apps/mapping-files-translation-transperfect/data/en/languageTranslations.json';
import { t } from 'i18next';
import { find } from 'lodash';
import { useContext, useEffect, useState } from 'react';

import { PersonalFit } from '../../../../../api/src/models/ProviderRecommendationDetails';
import { adobeLinkTrackEvent } from '../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../common/AdobeTagging/adobeModalTrackEvent';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { RecommendationProp } from '../../../common/PSXHeader/SmartChoice/PreferencesForm';
import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { useLagoon } from '../../../hooks/useLagoon';
import {
  ProviderDetails,
  ProviderLocation,
} from '../../../models/ProviderDetails';
import {
  RecommendationScoreMatch,
  getMatchDescription,
  getMatchText,
  removeSpecialCharactersUsingMatch,
} from '../../../utils/smartChoice.utils';
import { MatchText } from '../MatchText';
import { NotAMatchText } from '../NotAMatchText';
import {
  BreakdownContainer,
  ContainerWithTopandBottonBorder,
  CriteriaPointsContainer,
  CriteriaSubTitle,
  CriteriaTitle,
  DescriptionContainer,
  DrawerContentWithBackgroundgray,
  FAQContainer,
  FAQLink,
  StyledDescription,
} from '../SmartChoiceBreakDown.styles';
import { NotAMatchSearchIconAndText } from './Components/NotAMatchSearchIconAndText';
import {
  HealthGradeDescriptionContainer,
  StyledIcon,
  StyledLinkHealthGrade,
  StyledText,
} from './PersonalFitBreakDown.styles';

export interface PersonalFitBreakDownProps {
  personalFit: PersonalFit;
  preferencesData?: RecommendationProp;
  providerDetails?: ProviderDetails;
  locationKey?: string;
}

export const PersonalFitBreakDown = ({
  personalFit,
  providerDetails,
  preferencesData,
  locationKey,
}: PersonalFitBreakDownProps) => {
  const { portalData } = useContext(PortalContext);
  const [matchCount, setMatchCount] = useState(0);
  const [notMatchCount, setNotMatchCount] = useState(0);
  const [consumerRating, setConsumerRating] = useState('');
  const [consumerRatingSurveyVolume, setConsumerRatingSurveyVolume] =
    useState('');

  useEffect(() => {
    adobeModalTrackEvent('Smart choice personal fit breakdown');
  }, []);
  const isUmr = isUmrMember(portalData?.portalName);
  const baseUrl = isUmr ? config('UMR_BASE_URL') : config('BASE_URL');
  const providerData = providerDetails as ProviderDetails;
  const providerLocationData: ProviderLocation =
    providerDetails?.providerLocations?.find(
      (x) => x.locationKey === locationKey
    ) as ProviderLocation;
  const providerBoardCertSpecialty = providerData?.boardCertifications ?? [];
  const providerLanguages = providerData?.languagesSpoken ?? [];
  const providerLanguagesByOffice =
    providerLocationData?.languagesSpokenByOffice ?? [];
  const allProviderLanguages = [
    ...new Set(
      providerLanguages
        ?.concat(providerLanguagesByOffice)
        ?.map((item) => removeSpecialCharactersUsingMatch(item ?? ''))
    ),
  ];
  const providerRating = providerData?.healthGradeRating ?? '';
  const providerGender = providerData?.gender ?? '';
  const preferredLanguage = preferencesData?.languagesSpoken ?? '';
  const preferredGender = preferencesData?.providerGender ?? '';
  const preferredBoardCert = preferencesData?.providerBoardCertStatus ?? '';
  const GENDER_DATA = [
    { value: 'M', title: t('Male') },
    { value: 'F', title: t('Female') },
  ];
  const getFormattedPreferredGender = GENDER_DATA?.filter((item) =>
    preferredGender?.includes(item.value)
  )?.map((i) => i.title);
  const getFormattedProviderGender = providerGender?.replace(
    /(^\w{1})/g,
    (letter) => letter.toUpperCase()
  );
  const getFormattedPreferredLanguages =
    typeof preferredLanguage === 'string' &&
    preferredLanguage
      ?.split(',')
      ?.map((item) => enLangugages[item])
      ?.map((item) => removeSpecialCharactersUsingMatch(item));
  const isPreferredLanguage =
    allProviderLanguages?.length > 0 &&
    allProviderLanguages?.some((lang) =>
      getFormattedPreferredLanguages?.includes(lang)
    );
  const isPreferredGender =
    providerGender !== '' &&
    getFormattedPreferredGender.includes(getFormattedProviderGender);
  const isBoardCert = providerBoardCertSpecialty?.length > 0;
  const getMatchCounts = (personalFit: PersonalFit) => {
    let matchCount = 0;
    let notMatchCount = 0;

    if (preferredLanguage !== '') {
      if (isPreferredLanguage && allProviderLanguages?.length !== 0) {
        matchCount++;
      } else {
        notMatchCount++;
      }
    }

    if (preferredGender !== '') {
      if (
        providerGender === '' ||
        providerGender?.toLowerCase() === 'unknown'
      ) {
        notMatchCount++;
      } else if (isPreferredGender) {
        matchCount++;
      } else {
        notMatchCount++;
      }
    }

    if (preferredBoardCert === 'y') {
      if (isBoardCert) {
        matchCount++;
      } else {
        notMatchCount++;
      }
    }

    if (
      Boolean(personalFit?.consumerRatingMidPointStatus === 'Y') &&
      personalFit?.consumerRatingSurveyVolume !== '' &&
      personalFit?.consumerRatingSurveyVolume !== '0'
    ) {
      matchCount++;
    } else if (
      personalFit?.consumerRatingMidPointStatus === 'N' &&
      personalFit?.consumerRatingSurveyVolume !== '' &&
      personalFit?.consumerRatingSurveyVolume !== '0'
    ) {
      notMatchCount++;
    } else if (
      (personalFit?.consumerRatingSurveyVolume === '' ||
        personalFit?.consumerRatingSurveyVolume === '0') &&
      consumerRating !== ''
    ) {
      notMatchCount++;
    } else {
      notMatchCount++;
    }

    return { matchCount, notMatchCount };
  };

  const replacePlaceholders = (
    text: string | undefined,
    replacements: Record<string, string>
  ): string =>
    (text || '').replace(/\{\{(.*?)}}/g, (_, key) => {
      const keys = key.split('.');
      let value: any = replacements;
      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) break;
      }
      return typeof value === 'string' ? value : '';
    });
  const lagoonData = useLagoon('ui-messaging')();
  const description = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_DESCRIPTION,
  });
  const compatibility_title = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_COMPATABILITY_TITLE,
  });
  const speaks = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_SPEAKS,
  });
  const does_not_speak = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_DOES_NOT_SPEAKS_LANGUAGE,
  });
  const spoken_languages_unknown = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_LANGUAGE_UNKNOWN,
  });
  const provider_identifies_as = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_PROVIDER_IDENTIFIES_AS,
  });
  const provider_gender_unknown = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_GENDER_UNKNOWN,
  });
  const provider_board_certified = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_BOARD_CERTIFIED_IN,
  });
  const not_certified = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_NOT_BORD_CERTIFIED,
  });
  const paitient_experiance = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_PAITIENT_EXPERIENCE_TITLE,
  });
  const consumer_rating_and_stars = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_CONSIMER_RATING_STARS_ARE_PROVIDED_BY_HEALTHGRADES,
  });
  const consumer_rating = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_CONSUMER_RATING,
  });
  const rating_unavailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_CONSUMER_RATING_INFO_UNAVAILABLE,
  });
  const healthgrade_description = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_HEALTHGRADE_DESCRIPTION_AT_END,
  });
  const FAQs = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BREAKDOWN_FAQ_TEXT,
  });

  useEffect(() => {
    const { matchCount, notMatchCount } = getMatchCounts(personalFit);
    setMatchCount(matchCount);
    setNotMatchCount(notMatchCount);
    if (personalFit?.consumerRating) {
      setConsumerRating(providerRating);
    }
    if (personalFit.consumerRatingSurveyVolume) {
      setConsumerRatingSurveyVolume(personalFit?.consumerRatingSurveyVolume);
    }
  }, [personalFit]);

  const outOf = t('OUT_OF');
  const outOfTotal = getMatchText(
    personalFit?.scoreDetails?.match as string,
    `(${matchCount} ${outOf} ${matchCount + notMatchCount})`
  );

  return (
    <BreakdownContainer data-testid="personal-fit-breakdown">
      <DrawerContentWithBackgroundgray direction="column">
        <CriteriaTitle data-testid="match-description">
          {`${t(
            getMatchDescription(
              personalFit?.scoreDetails?.match ?? RecommendationScoreMatch.Low,
              'personalFit'
            )
          )} ${outOfTotal}`}
        </CriteriaTitle>
      </DrawerContentWithBackgroundgray>
      <DescriptionContainer>
        <StyledDescription data-testid="personal-fit-description">
          {description?.message}
        </StyledDescription>
      </DescriptionContainer>
      <CriteriaPointsContainer data-testid="criteria-points" direction="column">
        {(preferredLanguage !== '' ||
          preferredGender !== '' ||
          preferredBoardCert === 'y') && (
          <CriteriaSubTitle data-testid="compatability-title">
            {compatibility_title?.message}
          </CriteriaSubTitle>
        )}

        {preferredLanguage !== '' && (
          <>
            {personalFit?.languageSpoken === 'unknown' ? (
              <NotAMatchSearchIconAndText
                text={spoken_languages_unknown?.message}
              />
            ) : isPreferredLanguage && allProviderLanguages?.length !== 0 ? (
              <MatchText
                text={replacePlaceholders(speaks?.message, {
                  providerSpeaks: allProviderLanguages?.toString() || '',
                })}
              />
            ) : (
              <NotAMatchText
                text={replacePlaceholders(does_not_speak?.message, {
                  providerSpeaks: Array.isArray(getFormattedPreferredLanguages)
                    ? getFormattedPreferredLanguages.join(', ')
                    : '',
                })}
              />
            )}
          </>
        )}
        {preferredGender !== '' && (
          <>
            {providerGender === '' ||
            providerGender?.toLowerCase() === 'unknown' ? (
              <NotAMatchSearchIconAndText
                text={provider_gender_unknown?.message}
              />
            ) : isPreferredGender ? (
              <MatchText
                text={replacePlaceholders(provider_identifies_as?.message, {
                  providerGender: getFormattedProviderGender,
                })}
              />
            ) : (
              <NotAMatchText
                text={replacePlaceholders(provider_identifies_as?.message, {
                  providerGender: getFormattedProviderGender,
                })}
              />
            )}
          </>
        )}

        {preferredBoardCert === 'y' && (
          <>
            {isBoardCert ? (
              <MatchText
                text={replacePlaceholders(provider_board_certified?.message, {
                  providerBoardCertifications:
                    providerBoardCertSpecialty.join(', '),
                })}
              />
            ) : (
              <NotAMatchText text={not_certified?.message} />
            )}
          </>
        )}

        <CriteriaSubTitle data-testid="paitient-experiance">
          {paitient_experiance?.message}
        </CriteriaSubTitle>

        {Boolean(personalFit?.consumerRatingMidPointStatus === 'Y') &&
        personalFit?.consumerRatingSurveyVolume !== '' &&
        personalFit?.consumerRatingSurveyVolume !== '0' ? (
          <MatchText
            text={replacePlaceholders(consumer_rating_and_stars?.message, {
              consumerRating,
              consumerRatingSurveyVolume,
            })}
          />
        ) : personalFit?.consumerRatingMidPointStatus === 'N' &&
          personalFit?.consumerRatingSurveyVolume !== '' &&
          personalFit?.consumerRatingSurveyVolume !== '0' ? (
          <NotAMatchText
            text={replacePlaceholders(consumer_rating_and_stars?.message, {
              consumerRating,
              consumerRatingSurveyVolume,
            })}
          />
        ) : (personalFit?.consumerRatingSurveyVolume === '' ||
            personalFit?.consumerRatingSurveyVolume === '0') &&
          consumerRating !== '' ? (
          <NotAMatchText
            text={replacePlaceholders(consumer_rating?.message, {
              consumerRating,
              consumerRatingSurveyVolume,
            })}
          />
        ) : (
          <NotAMatchSearchIconAndText text={rating_unavailable?.message} />
        )}
      </CriteriaPointsContainer>
      <ContainerWithTopandBottonBorder
        data-testid="healthgrades-container"
        direction="column"
      >
        <StyledIcon
          icon="healthgrades"
          size={132}
          title={t('HEALTH_GRADES_LOGO_TITLE')}
        />
        <HealthGradeDescriptionContainer data-testid="healthgrades-description">
          <StyledText>{healthgrade_description?.message}</StyledText>
          <StyledLinkHealthGrade
            data-testid="healthgrades-link"
            href="https://www.healthgrades.com"
            onClick={() =>
              adobeLinkTrackEvent({
                name: 'healthgrade',
                location: 'Smart choice personal fit breakdown',
              })
            }
            openNewWindow
          >
            {''}
          </StyledLinkHealthGrade>
        </HealthGradeDescriptionContainer>
      </ContainerWithTopandBottonBorder>
      <FAQContainer>
        <FAQLink
          data-testid="faq-link"
          href={`${baseUrl}/findcare/faq`}
          onClick={() => {
            adobeLinkTrackEvent({
              name: 'Faqs',
              location: 'Smart choice personal fit breakdown',
            });
          }}
        >
          {FAQs?.message}
        </FAQLink>
      </FAQContainer>
    </BreakdownContainer>
  );
};
